import React, { useEffect, useRef } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';

export interface NotFoundColors {
  ink: string;
  green: string;
  paper: string;
  wash: string;
  muted: string;
}

export interface NotFoundPageProps {
  homeHref?: string;
  dashboardHref?: string;
  colors?: Partial<NotFoundColors>;
}

const DEFAULT_COLORS: NotFoundColors = {
  ink: '#0c0c1a',
  green: '#16a34a',
  paper: '#fbfcfa',
  wash: 'rgba(22, 163, 74, 0.10)',
  muted: '#64617a',
};

/* Route in a 1200 x 400 viewBox: envelope (left) -> loop -> 404 sign (right) */
const ROUTE =
  'M170 300 C 230 170, 330 120, 420 190 S 520 330, 610 270 C 680 220, 650 140, 595 160 C 540 180, 575 270, 665 265 S 850 150, 960 175 S 1110 110, 1210 70';

const CYCLE = 7.5; // seconds per flight
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const seg = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

function SafeLink({ to, className, children }: { to: string; className?: string; children: React.ReactNode }) {
  const inRouter = useInRouterContext();
  if (inRouter && !to.startsWith('http') && !to.startsWith('//')) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={to} className={className}>
      {children}
    </a>
  );
}

export default function NotFoundPage({
  homeHref = '/',
  dashboardHref = '/dashboard',
  colors,
}: NotFoundPageProps) {
  const c: NotFoundColors = { ...DEFAULT_COLORS, ...colors };
  const els = useRef<Record<string, SVGElement | null>>({});
  const r = (k: string) => (el: SVGElement | null) => {
    els.current[k] = el;
  };

  useEffect(() => {
    const path = els.current.route as unknown as SVGPathElement | null;
    if (!path || typeof path.getTotalLength !== 'function') return;
    const total = path.getTotalLength();
    const set = (k: string, a: Record<string, string | number>) => {
      const el = els.current[k];
      if (!el) return;
      for (const n in a) el.setAttribute(n, String(a[n]));
    };

    const frame = (t: number) => {
      /* ---- plane along route ---- */
      const phase = (t % CYCLE) / CYCLE;
      const flight = easeInOut(seg(phase, 0.04, 0.9));
      const s = flight * total;
      const p = path.getPointAtLength(s);
      const q = path.getPointAtLength(Math.min(total, s + 2));
      const ang = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;
      const fade = easeOut(seg(phase, 0.04, 0.12)) * (1 - seg(phase, 0.86, 0.94));
      const wob = Math.sin(t * 5) * 2;
      set('plane', {
        transform: `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)}) rotate(${(ang + wob).toFixed(2)})`,
        opacity: fade,
      });

      /* trail: fading dashes drawn up to the plane */
      set('trail', {
        'stroke-dasharray': `${Math.max(0, s).toFixed(1)} ${total}`,
        opacity: 0.55 * (1 - seg(phase, 0.86, 0.96)),
      });

      /* ---- envelope: idle bob, flap pulse at launch ---- */
      const bob = Math.sin(t * 2) * 4;
      const kick = Math.sin(Math.PI * seg(phase, 0, 0.1));
      set('env', {
        transform: `translate(150 ${(302 + bob - kick * 8).toFixed(2)}) rotate(${(-12 + kick * -6).toFixed(2)})`,
      });
      set('envShadow', { rx: 38 - bob * 1.2 - kick * 6, 'fill-opacity': 0.08 });
      set('flap', { transform: `scale(1 ${(1 - kick * 0.55).toFixed(3)})` });

      /* ---- sign: sway + little "bonk" when plane flies by ---- */
      const near = Math.sin(Math.PI * seg(phase, 0.72, 0.92));
      set('sign', {
        transform: `translate(1040 250) rotate(${(Math.sin(t * 1.4) * 1.2 + near * 3).toFixed(2)})`,
      });

      /* ---- sparkles around sign ---- */
      for (let i = 0; i < 3; i++) {
        const k = Math.abs(Math.sin(t * 1.3 + i * 1.1));
        set(`sp${i}`, { opacity: 0.25 + 0.75 * k, transform: `scale(${(0.6 + 0.4 * k).toFixed(3)})` });
      }
    };

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      frame(CYCLE * 0.55);
      return;
    }

    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      frame((now - t0) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const common = { stroke: c.ink, strokeWidth: 4, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };

  return (
    <main
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px 24px',
        overflow: 'hidden',
        background: c.paper,
        backgroundImage: 'radial-gradient(rgba(12,12,26,0.07) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        fontFamily: 'var(--font-ui, Inter, system-ui, -apple-system, "Segoe UI", sans-serif)',
        color: c.ink,
      }}
    >
      <style>{`
        .nf-btn{display:inline-flex;align-items:center;gap:8px;height:48px;padding:0 24px;border-radius:10px;
          font-size:15px;font-weight:500;text-decoration:none;transition:transform .15s ease,box-shadow .15s ease,background .15s ease}
        .nf-btn:hover{transform:translateY(-1px)}
        .nf-btn:focus-visible{outline:2px solid ${c.green};outline-offset:3px}
        .nf-primary{background:${c.green};color:#fff;box-shadow:0 6px 16px -6px rgba(22,163,74,.55)}
        .nf-primary:hover{box-shadow:0 10px 22px -6px rgba(22,163,74,.6)}
        .nf-primary svg{transition:transform .15s ease}
        .nf-primary:hover svg{transform:translateX(3px)}
        .nf-ghost{background:#fff;color:${c.ink};border:1px solid rgba(12,12,26,.12)}
        .nf-ghost:hover{background:#f6f8f5}
        @keyframes nf-rise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        .nf-in{animation:nf-rise .7s cubic-bezier(.2,.7,.2,1) both}
        @media (prefers-reduced-motion: reduce){.nf-in{animation:none}.nf-btn{transition:none}}
      `}</style>

      <section style={{ textAlign: 'center', maxWidth: 640, zIndex: 1 }}>
        <p
          className="nf-in"
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: c.muted,
          }}
        >
          404 — Not Found
        </p>

        <h1
          className="nf-in"
          style={{
            animationDelay: '.08s',
            margin: '20px 0 0',
            fontFamily: 'var(--font-display, "Newsreader", Georgia, "Times New Roman", serif)',
            fontSize: 'clamp(40px, 7vw, 68px)',
            lineHeight: 1.05,
            fontWeight: 500,
            letterSpacing: '-0.02em',
          }}
        >
          Looks like this one
          <br />
          <em style={{ color: c.green, fontStyle: 'italic' }}>flew off course.</em>
        </h1>

        <p
          className="nf-in"
          style={{
            animationDelay: '.16s',
            margin: '24px auto 0',
            fontSize: 18,
            lineHeight: 1.6,
            color: c.muted,
            maxWidth: 440,
          }}
        >
          The page you’re looking for couldn’t be delivered. Let’s get you back on the right route.
        </p>

        <div
          className="nf-in"
          style={{
            animationDelay: '.24s',
            marginTop: 32,
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <SafeLink to={homeHref} className="nf-btn nf-primary">
            Back to Homepage
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </SafeLink>
          <SafeLink to={dashboardHref} className="nf-btn nf-ghost">
            Go to Dashboard
          </SafeLink>
        </div>
      </section>

      {/* ───────── Scene ───────── */}
      <svg
        viewBox="0 0 1200 400"
        fill="none"
        aria-hidden="true"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: 1200, height: 'auto', marginTop: 8, overflow: 'visible' }}
      >
        {/* dashed route + progress trail */}
        <path
          ref={r('route')}
          d={ROUTE}
          stroke={c.green}
          strokeWidth={2.5}
          strokeDasharray="6 9"
          strokeLinecap="round"
          opacity={0.35}
        />
        <path
          ref={r('trail')}
          d={ROUTE}
          stroke={c.green}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray="0 3000"
          opacity={0}
        />

        {/* envelope */}
        <ellipse ref={r('envShadow')} cx="150" cy="352" rx="38" ry="6" fill={c.ink} fillOpacity={0.08} />
        <g ref={r('env')} transform="translate(150 302) rotate(-12)">
          <rect x="-48" y="-30" width="96" height="60" rx="6" fill={c.paper} {...common} />
          <g ref={r('flap')} style={{ transformOrigin: '0px -30px', transformBox: 'view-box' as never }}>
            <polygon points="-48,-30 48,-30 0,6" fill={c.wash} {...common} />
          </g>
          <polygon points="0,-6 7,0 0,6 -7,0" fill={c.green} />
          {/* speed lines */}
          <line x1="-62" y1="-10" x2="-90" y2="-10" stroke={c.ink} strokeWidth={3} strokeLinecap="round" opacity={0.6} />
          <line x1="-62" y1="2" x2="-98" y2="2" stroke={c.green} strokeWidth={3} strokeLinecap="round" opacity={0.7} />
          <line x1="-62" y1="14" x2="-84" y2="14" stroke={c.ink} strokeWidth={3} strokeLinecap="round" opacity={0.5} />
        </g>

        {/* 404 sign */}
        <ellipse cx="1040" cy="352" rx="62" ry="7" fill={c.wash} />
        <g ref={r('sign')} transform="translate(1040 250)">
          <line x1="0" y1="-10" x2="0" y2="100" stroke={c.ink} strokeWidth={5} strokeLinecap="round" />
          <g style={{ transformOrigin: '0 0' }}>
            <rect x="-56" y="-70" width="112" height="62" rx="8" fill={c.paper} {...common} />
            <text
              x="0"
              y="-26"
              textAnchor="middle"
              fontSize="34"
              fontWeight="700"
              fill={c.ink}
              style={{ fontFamily: 'var(--font-ui, Inter, system-ui, sans-serif)' }}
            >
              404
            </text>
          </g>
          <path d="M-18 100 l-6 -14 M-6 100 l-2 -18 M10 100 l6 -14 M22 100 l10 -10" stroke={c.green} strokeWidth={3} strokeLinecap="round" />
        </g>

        {/* sparkles near sign */}
        {[
          { x: 975, y: 150 },
          { x: 1110, y: 140 },
          { x: 1130, y: 260 },
        ].map((s, i) => (
          <g key={i} transform={`translate(${s.x} ${s.y})`}>
            <path
              ref={r(`sp${i}`)}
              d="M-6 0H6M0 -6V6"
              stroke={i === 1 ? c.green : c.ink}
              strokeWidth={3}
              strokeLinecap="round"
            />
          </g>
        ))}

        {/* paper plane (nose points +x; rotated along the route tangent) */}
        <g ref={r('plane')} opacity={0}>
          <polygon points="-34,-22 40,0 -8,4" fill={c.paper} {...common} />
          <polygon points="-8,4 40,0 12,22 -22,28" fill={c.ink} {...common} />
          <polygon points="-8,4 14,6 -12,22" fill={c.green} />
        </g>
      </svg>
    </main>
  );
}
